import { Grid, Stack } from "@mui/material";
import { useTheme } from "design-system/themeContext";
import React, { useContext } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { Navigate, useParams } from "react-router-dom";

import { IsAllowed } from "../acl";
import { resources } from "../acl/resources";
import { LoadingOrEmptyWrapper } from "../components/LoadingAndEmptyHandler";
import { Action } from "../features/dt-basic-setup/single-building/components/action/Action";
import { AddressStep } from "../features/dt-basic-setup/single-building/components/address-step/AddressStep";
import { BuildingDetailsStep } from "../features/dt-basic-setup/single-building/components/details-step/BuildingDetailsStep";
import { FileUploadStep } from "../features/dt-basic-setup/single-building/components/file-upload-step/FileUploadStep";
import { FinalStep } from "../features/dt-basic-setup/single-building/components/final-step/FinalStep";
import { LayersStep } from "../features/dt-basic-setup/single-building/components/layer-step/LayerStep";
import { AddSingleBuildingStepper } from "../features/dt-basic-setup/single-building/components/layout/AddSingleBuildingStepper";
import { SupportChatButton } from "../features/dt-basic-setup/single-building/components/layout/SupportChatButton";
import { OutlineDataGetter } from "../features/dt-basic-setup/single-building/components/outline-step/OutlineDataGetter";
import { OutlineStep } from "../features/dt-basic-setup/single-building/components/outline-step/OutlineStep";
import { SummaryStep } from "../features/dt-basic-setup/single-building/components/summary-step/SummaryStep";
import {
  SingleBuildingContext,
  SingleBuildingContextProvider,
} from "../features/dt-basic-setup/single-building/context/AddSingleBuildingContext";
import { STEP } from "../features/dt-basic-setup/single-building/model/constants";

export const AddSingleBuildingPage = () => {
  const { projectId } = useParams();

  if (!IsAllowed(resources.ADD_BUILDING.VIEW)) return <Navigate replace to="/" />;

  return (
    <SingleBuildingContextProvider projectId={projectId}>
      <AddSingleBuildingComponent />
    </SingleBuildingContextProvider>
  );
};

export const AddSingleBuildingComponent = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { isPrefillingFormData, pauseInteraction, setStepNo, stepNo } = useContext(SingleBuildingContext);
  const showStepper = stepNo < STEP.final;

  return (
    <>
      <Helmet>
        <title>Add building | {(theme as any)?.clientDisplayName}</title>
      </Helmet>
      <section className="py-2">
        {showStepper && (
          <AddSingleBuildingStepper changeStep={(stepNo) => setStepNo(stepNo - 1)} currentStep={stepNo + 1} />
        )}
        <LoadingOrEmptyWrapper
          loadingText={t("AddBuilding.prefillingFormDataLoadingText") ?? ""}
          showLoading={isPrefillingFormData || pauseInteraction}
        >
          <Stack className="mt-18 justify-center">
            <Grid className="w-full" columnSpacing={4} container>
              {stepNo === STEP.address && <AddressStep />}
              {stepNo === STEP.summary && <SummaryStep />}
              {stepNo <= STEP.outline && <OutlineDataGetter />}
              {stepNo === STEP.outline && <OutlineStep />}
              {stepNo === STEP.layers && <LayersStep />}
              {stepNo === STEP.buildingDetails && <BuildingDetailsStep />}
              {stepNo === STEP.fileUpload && <FileUploadStep />}
              {stepNo === STEP.final && <FinalStep />}
            </Grid>
          </Stack>
        </LoadingOrEmptyWrapper>
        <SupportChatButton />
        <Action />
      </section>
    </>
  );
};
